import { useMutation } from "@tanstack/react-query";
import { useReservation } from "api/api-hooks/use-create-reservation";
import { post } from "api/api-methods";
import { getLocaleFromLang } from "helpers/language-helper";
import { useBookingId } from "hooks/use-booking-id";
import { useBookingStateContext } from "hooks/use-booking-state";
import { useError } from "hooks/use-error";
import i18n from "i18next";
import { sendToParent, trackEvent, error as logError } from "logger/logger";
import queryClient from "query-client";
import { useNavigate, useParams } from "react-router-dom";
import { UrlParamName } from "search-params";
import { State } from "store/store-types";
import { CreateReservationParameters } from "types/request-parameters";
import { WebBooking } from "types/web-booking";
import { TrackingEventType } from "logger/event-utils";

// Parameters to exclude when preserving URL parameters after booking finalization
const PARAMS_TO_EXCLUDE = [
    UrlParamName.RULE_ID,
    UrlParamName.DATE,
    UrlParamName.GUESTS,
    UrlParamName.GUESTS_CHILDREN,
    UrlParamName.IS_TIMES_CONFIRMED,
    UrlParamName.IS_WAIT_LIST,
];

export const useFinalizeBooking = () => {
    const { system } = useParams();
    const navigate = useNavigate();
    const { openReservationFailedModal } = useError();
    const { data: reservation } = useReservation();
    const bookingId = useBookingId();
    const { isActivityBooking } = useBookingStateContext();

    const finalizeBooking = async (state: State) => {
        if (!bookingId || !reservation) return;
        const data: CreateReservationParameters = {
            ...state,
            lang: getLocaleFromLang(i18n.language),
            logMessage: "Bokning skapad",
            message: state.contact?.message,
            times: reservation.getBookTimes().map((time) => {
                return {
                    ruleId: time.ruleId,
                    start: time.start.toISOString(),
                };
            }),
        };

        let eventName = "BookingFinalized";
        const startTime = new Date().getTime();

        try {
            const booking = await post<WebBooking, CreateReservationParameters>(
                `WebBooking/WebBookings/${bookingId}/Finalize`,
                data
            );

            const currentParams = new URLSearchParams(window.location.search);
            const searchParamsToKeep = Array.from(currentParams.entries())
                .filter(([key]) => !PARAMS_TO_EXCLUDE.includes(key as UrlParamName))
                .reduce((params, [key, value]) => {
                    params.set(key, value);
                    return params;
                }, new URLSearchParams())
                .toString();

            const searchParamsString = searchParamsToKeep ? `?${searchParamsToKeep}` : "";

            let pathname = `/booking/${system}`;
            if (isActivityBooking) pathname += "/activity-booking";
            pathname += `/${bookingId}`;
            pathname += searchParamsString;

            void navigate(pathname, {
                state: { isFinal: true },
            });

            // TODO: revisit the scroll event implementation. (logic brought from previous widget 3.0)
            document.documentElement.scrollTop = 0;
            document.body.scrollTop = 0;
            window.parent.postMessage("scroll2top", "*");

            setTimeout(() => {
                queryClient.setQueryData<WebBooking>(
                    ["booking", bookingId],
                    new WebBooking(booking)
                );
                // Use timeout to ensure the state is updated after navigation
            }, 50);

            const endTime = new Date().getTime();
            const loadTime = endTime - startTime;
            trackEvent(eventName, {
                guests: state.guests ?? 0,
                children: state.guestsChildren ?? 0,
                date: state.times?.[0]?.start.toISOString() ?? "",
                isWaitingList: (state.times?.length ?? 0) > 1,
                ms: loadTime,
            });
            sendToParent(state, TrackingEventType.bookingFinalized);
            return booking;
        } catch (error) {
            logError(error);
            eventName = "BookingFinalizeFailed";
            openReservationFailedModal();

            const endTime = new Date().getTime();
            const loadTime = endTime - startTime;
            trackEvent(eventName, {
                guests: state.guests ?? 0,
                children: state.guestsChildren ?? 0,
                date: state.times?.[0]?.start.toISOString() ?? "",
                isWaitingList: (state.times?.length ?? 0) > 1,
                ms: loadTime,
            });
            sendToParent(state, TrackingEventType.bookingFinalizedFailed);
            throw error;
        }
    };

    return useMutation({
        mutationFn: finalizeBooking,
        scope: {
            id: "webbooking-create",
        },
    });
};
